import { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Sparkles, MessageCircle, Send, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import AiQuoteDialog from "../components/AiQuoteDialog";
import { AttachButton, PendingAttachments, MessageAttachments } from "../components/ChatAttachments";
import { toast } from "sonner";

const STATUS_COLORS = {
  "nuovo": "bg-blue-100 text-blue-800",
  "in_lavorazione": "bg-amber-100 text-amber-800",
  "chiuso": "bg-emerald-100 text-emerald-800",
  "annullato": "bg-slate-200 text-slate-700",
};

export default function UserDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [aiOpen, setAiOpen] = useState(false);
  const [chatQuote, setChatQuote] = useState(null);

  const fetchQuotes = async () => {
    try {
      const r = await api.get("/quotes/me");
      setQuotes(r.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (user) fetchQuotes();
  }, [user]);

  if (loading) return <div className="min-h-screen"><Navbar/><div className="p-12 text-center text-slate-500">Caricamento...</div></div>;

  if (!user) {
    return (
      <div className="min-h-screen">
        <Navbar/>
        <div className="max-w-2xl mx-auto p-12 text-center">
          <h1 className="heading text-3xl font-extrabold text-slate-900">Accedi per vedere le tue richieste</h1>
          <p className="text-slate-600 mt-3">Con l'accesso puoi seguire lo stato dei tuoi preventivi e chattare con il personale.</p>
          <div className="flex gap-3 justify-center mt-6">
            <Button onClick={() => navigate("/login")} data-testid="user-login-btn" className="bg-blue-800 hover:bg-blue-900">Accedi</Button>
            <Button onClick={() => navigate("/register")} variant="outline" data-testid="user-register-btn">Registrati</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar/>
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mt-6 mb-8">
          <div>
            <h1 className="heading text-3xl md:text-4xl font-extrabold text-slate-900">Ciao {user.name?.split(" ")[0]}</h1>
            <p className="text-slate-600">Ecco le tue richieste di preventivo</p>
          </div>
          <Button onClick={() => setAiOpen(true)} data-testid="user-new-quote-btn"
                  className="bg-amber-500 hover:bg-amber-600 text-white">
            <Sparkles className="w-4 h-4 mr-2"/> Nuova richiesta
          </Button>
        </div>

        {quotes.length === 0 ? (
          <Card className="p-12 text-center border-dashed">
            <p className="text-slate-600">Non hai ancora richieste. Iniziamo?</p>
            <Button onClick={() => setAiOpen(true)} className="mt-4 bg-blue-800">Richiedi un preventivo</Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="user-quotes-list">
            {quotes.map(q => (
              <Card key={q.quote_id} className="p-5 hover:shadow-md transition-shadow" data-testid={`user-quote-${q.quote_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-slate-500 font-mono">{q.quote_id}</div>
                    <div className="heading font-bold text-lg text-slate-900 mt-1">{q.category}</div>
                    <div className="text-sm text-slate-600 mt-1">{q.city}</div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[q.status] || "bg-slate-100"}`}>{q.status}</span>
                </div>
                <p className="text-sm text-slate-700 mt-3 line-clamp-3">{q.summary}</p>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                  <span className="text-xs text-slate-500">{new Date(q.created_at).toLocaleDateString("it-IT")}</span>
                  <Button size="sm" variant="outline" onClick={() => setChatQuote(q)} data-testid={`user-chat-btn-${q.quote_id}`}>
                    <MessageCircle className="w-4 h-4 mr-2"/> Chat
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
      <AiQuoteDialog open={aiOpen} onOpenChange={(v) => { setAiOpen(v); if (!v) fetchQuotes(); }} />
      <ChatDialog quote={chatQuote} onClose={() => setChatQuote(null)} me={user}/>
    </div>
  );
}

function ChatDialog({ quote, onClose, me }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState([]);

  const load = async () => {
    if (!quote) return;
    try {
      const r = await api.get(`/messages/${quote.quote_id}`);
      setMessages(r.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); setPending([]); }, [quote]);
  useEffect(() => {
    if (!quote) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [quote]);

  const send = async () => {
    if (!text.trim() && pending.length === 0) return;
    setSending(true);
    try {
      await api.post(`/messages/${quote.quote_id}`, {
        content: text.trim(),
        attachments: pending.map(p => p.file_id),
      });
      setText("");
      setPending([]);
      await load();
    } catch { toast.error("Errore invio"); } finally { setSending(false); }
  };

  return (
    <Dialog open={!!quote} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg h-[70vh] flex flex-col p-0">
        <DialogHeader className="p-4 border-b bg-blue-800 rounded-t-lg">
          <DialogTitle className="text-white heading">Chat con il personale</DialogTitle>
          <p className="text-blue-100 text-xs">{quote?.quote_id} · {quote?.category}</p>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50" data-testid="user-chat-messages">
          {messages.length === 0 && <div className="text-center text-sm text-slate-500 mt-8">Nessun messaggio ancora. Scrivi tu il primo!</div>}
          {messages.map(m => {
            const mine = m.from_user_id === me?.user_id;
            return (
              <div key={m.msg_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${mine ? "bg-blue-800 text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                  <div className="text-[10px] opacity-70 mb-0.5">{m.from_name}{m.from_staff && " · Staff"}</div>
                  {m.content && <div className="whitespace-pre-wrap">{m.content}</div>}
                  <MessageAttachments attachments={m.attachments} side={mine ? "right" : "left"}/>
                </div>
              </div>
            );
          })}
        </div>
        <PendingAttachments items={pending} onRemove={(id) => setPending(p => p.filter(x => x.file_id !== id))}/>
        <div className="p-3 border-t flex gap-2 items-center">
          <AttachButton onUploaded={(a) => setPending(p => [...p, ...a])} testId="user-chat-attach"/>
          <Input value={text} onChange={e=>setText(e.target.value)} onKeyDown={e=>e.key==="Enter" && send()}
                 placeholder="Scrivi un messaggio..." data-testid="user-chat-input"/>
          <Button onClick={send} disabled={sending || (!text.trim() && pending.length === 0)} data-testid="user-chat-send" className="bg-blue-800">
            {sending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
